import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import ButtonStartIcon from "../../../../../../button/ButtonStartIcon";
import InputField from "../../../../../../FormControl/input/InputField";
import Dialog from "../../../../../../dialog/Dialog";
import useApiServices from "../../../../../../../hooks/setting/useApiServices";
import {
  Alert,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import TextCopyClipboard from "../../../../../../text/TextCopyClipboard";
import TextEllipsis from "../../../../../../text/TextEllipsis";

const ApiServicesDialog = ({ show, onClose, type, data = {} }) => {
  const {
    errors: errorsData,
    createApiServices,
    deleteApiServices,
  } = useApiServices();

  const listRestrictions = ["import", "sync", "migration", "task"];

  const [form, setInputForm] = useState({});
  const [errors, setInputErrors] = useState(errorsData);
  const [loading, toggleLoading] = useState(false);

  const onChange = (name, value) => {
    if (name === "permissions") {
      const { name, checked } = value;
      let permissions = form.permissions || [];
      if (checked) {
        permissions.push(name);
      } else {
        permissions = permissions.filter((item) => item !== name);
      }
      setInputForm({ ...form, permissions });
      setInputErrors({ ...errors, [name]: null });
      return;
    }
    setInputForm({ ...form, [name]: value });
    setInputErrors({ ...errors, [name]: null });
  };

  const onSave = async (e) => {
    if (!["create", "delete"].includes(type)) {
      setInputErrors({ message: "Invalid type" });
      return;
    }
    e.preventDefault();
    toggleLoading(true);
    try {
      let result = { error: false };
      if (type === "create") {
        result = await createApiServices(form);
      }
      if (type === "delete") {
        result = await deleteApiServices(form._id);
      }
      if (result.error) {
        setInputErrors(result);
      } else {
        onClose();
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    setInputErrors(errorsData);
  }, [errorsData]);

  useEffect(() => {
    setInputForm(data || {});
  }, [data]);

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color="primary"
        variant="outlined"
        disabled={loading}
        startIcon={["fal", "times"]}
      >
        Cancel
      </ButtonStartIcon>
      {["create", "edit", "delete"].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color="primary"
          variant="contained"
          disabled={loading}
          loading={loading}
          startIcon={["fal", "check"]}
        >
          {type === "delete" ? "Confirm" : "Save"}
        </ButtonStartIcon>
      )}
    </>
  );

  return (
    <Dialog
      id={`${type}-api-kyes`}
      show={show}
      title={`${type} API Key`}
      onClose={onClose}
      actions={Actions}
    >
      {type === "delete" ? (
        <Typography>
          Are you sure you want to delete the credential: <b>{form.name}</b>?
        </Typography>
      ) : (
        <div className="flex flex-col gap-4 py-4">
          <div className="grid grid-cols-3 items-center gap-4">
            {form._id && (
              <>
                <Typography
                  variant="inherit"
                  className="font-bold"
                  color="primary"
                  align="right"
                  component="p"
                >
                  Name
                </Typography>
                <div className="col-span-2">
                  {type === "preview" ? (
                    <Typography>{form.name}</Typography>
                  ) : (
                    <InputField
                      id="name"
                      name="name"
                      value={form.name}
                      error={errors?.name}
                      helperText={errors?.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
              </>
            )}
            <Typography
              variant="inherit"
              className="font-bold"
              color="primary"
              align="right"
              component="p"
            >
              Description
            </Typography>
            <div className="col-span-2">
              {type === "preview" ? (
                <Typography>{form.description}</Typography>
              ) : (
                <InputField
                  id="description"
                  name="description"
                  value={form.description}
                  error={errors?.description}
                  helperText={errors?.description}
                  onChange={(e) => onChange("description", e.target.value)}
                  fullWidth
                />
              )}
            </div>
            {form._id && (
              <>
                <Typography
                  variant="inherit"
                  className="font-bold"
                  color="primary"
                  align="right"
                  component="p"
                >
                  API Key
                </Typography>
                <div className="col-span-2">
                  <TextCopyClipboard text={form.apiKey.toString()} />
                </div>
              </>
            )}
            <Typography
              variant="inherit"
              className="font-bold"
              color="primary"
              align="right"
              component="p"
            >
              Permissions
            </Typography>
            <div className="col-span-2 flex flex-col gap-2">
              {form._id ? (
                <>
                  {form?.permissions?.length > 0 ? (
                    form?.permissions?.map((item, index) => (
                      <Chip
                        key={index}
                        label={item}
                        color="primary"
                        variant="outlined"
                        className="capitalize"
                        component={"span"}
                      />
                    ))
                  ) : (
                    <Typography className="text-muted-foreground opacity-50">
                      No permissions
                    </Typography>
                  )}
                </>
              ) : (
                listRestrictions.map((item, index) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        id={`checkbox-permissions-${index}-${index}`}
                        checked={
                          form?.permissions?.includes(item) ? true : false
                        }
                        name={item}
                        onChange={(e) =>
                          onChange("permissions", {
                            name: item,
                            checked: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label={
                      <TextEllipsis
                        id={`text-roles-${index}-${item}`}
                        className="capitalize"
                      >
                        {item}
                      </TextEllipsis>
                    }
                  />
                ))
              )}
              {errors?.permissions && (
                <FormHelperText error={errors?.permissions}>
                  {errors?.permissions}
                </FormHelperText>
              )}
            </div>
          </div>
        </div>
      )}
      {errors && errors.message && (
        <Alert
          severity="error"
          onClose={() => setInputErrors({ message: null })}
        >
          {errors.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default ApiServicesDialog;
